// =============================================================================
// src/components/settings/WebsiteGalleryTab.jsx
// Centralized management view for website-approved photos.
// Lets you unapprove or unfeature photos without digging into individual jobs.
// =============================================================================

import { useState, useEffect, useCallback } from 'react'
import {
  Globe2, Star, ExternalLink, Loader2, ImageOff,
  AlertCircle, RefreshCw, Eye, EyeOff
} from 'lucide-react'
import jobPhotoService from '../../services/jobPhotoService'

const FILTERS = [
  { id: 'all', label: 'All approved' },
  { id: 'featured', label: 'Featured (homepage)' },
  { id: 'before', label: 'Before photos' },
  { id: 'after', label: 'After photos' },
]

export default function WebsiteGalleryTab({ showToast }) {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [toggling, setToggling] = useState(null) // photoId currently being toggled

  const loadPhotos = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await jobPhotoService.getWebsiteApprovedPhotos()
      setPhotos(response.data)
    } catch (err) {
      console.error('Error loading approved photos:', err)
      setError('Failed to load gallery photos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPhotos()
  }, [loadPhotos])

  const handleUnapprove = async (photo) => {
    if (toggling) return
    setToggling(photo.id)
    try {
      await jobPhotoService.toggleWebsiteApproval(photo.id, false)
      // Remove from list since it's no longer approved
      setPhotos(prev => prev.filter(p =>
        p.id !== photo.id && p.pairedWithPhotoId !== photo.id
      ))
      showToast?.('Photo removed from website gallery')
    } catch (err) {
      console.error('Error unapproving photo:', err)
      showToast?.('Failed to remove photo', 'error')
    } finally {
      setToggling(null)
    }
  }

  const handleToggleFeatured = async (photo) => {
    if (toggling) return
    setToggling(photo.id)
    try {
      const newValue = !photo.isFeatured
      await jobPhotoService.toggleFeatured(photo.id, newValue)
      setPhotos(prev => prev.map(p =>
        p.id === photo.id ? { ...p, isFeatured: newValue } : p
      ))
      showToast?.(newValue ? 'Added to homepage rotation' : 'Removed from homepage rotation')
    } catch (err) {
      console.error('Error toggling featured:', err)
      showToast?.('Failed to update featured status', 'error')
    } finally {
      setToggling(null)
    }
  }

  const filteredPhotos = photos.filter(photo => {
    if (filter === 'featured') return photo.isFeatured
    if (filter === 'before') return photo.type === 'Before'
    if (filter === 'after') return photo.type === 'After'
    return true
  })

  const featuredCount = photos.filter(p => p.isFeatured).length
  const totalCount = photos.length

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle size={32} className="mx-auto text-red-400 mb-3" />
        <p className="text-red-600 font-medium mb-4">{error}</p>
        <button onClick={loadPhotos} className="btn btn-secondary inline-flex items-center gap-2">
          <RefreshCw size={16} /> Try again
        </button>
      </div>
    )
  }

  // ── Empty state ────────────────────────────────────────────────────────────

  if (photos.length === 0) {
    return (
      <div className="card">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Website Gallery</h3>
          <p className="text-sm text-gray-500">
            Manage photos that appear on your public website gallery.
          </p>
        </div>
        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <ImageOff size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium mb-1">No approved photos yet</p>
          <p className="text-gray-400 text-sm max-w-xs mx-auto">
            Open a job, expand the Photos section, and click the{' '}
            <Globe2 size={13} className="inline" /> icon on any Before/After pair to approve it for your website.
          </p>
        </div>
      </div>
    )
  }

  // ── Main view ──────────────────────────────────────────────────────────────

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Website Gallery</h3>
          <p className="text-sm text-gray-500">
            {totalCount} approved photo{totalCount !== 1 ? 's' : ''} &bull;{' '}
            {featuredCount} featured on homepage
          </p>
        </div>
        <button
          onClick={loadPhotos}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl mb-6">
        <Globe2 size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-blue-700 leading-relaxed">
          <span className="font-semibold">Globe</span> = visible on your gallery page &nbsp;&bull;&nbsp;
          <span className="font-semibold">Star</span> = also shown in the homepage rotation (max 6).
          Unapproving a Before photo also removes its paired After.
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.id
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
            {f.id === 'featured' && featuredCount > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                filter === 'featured' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {featuredCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Empty filter state */}
      {filteredPhotos.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-400 text-sm">No photos match this filter</p>
        </div>
      )}

      {/* Photo grid */}
      {filteredPhotos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filteredPhotos.map(photo => {
            const isToggling = toggling === photo.id
            return (
              <div
                key={photo.id}
                className="group relative rounded-xl overflow-hidden bg-gray-100 border border-gray-200"
              >
                {/* Photo */}
                <div className="aspect-square">
                  <img
                    src={photo.thumbnailUrl || photo.url}
                    alt={photo.caption || photo.type}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Type + featured badge */}
                <div className="absolute top-2 left-2 flex items-center gap-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    photo.type === 'Before'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {photo.type}
                  </span>
                  {photo.isFeatured && (
                    <span className="bg-amber-400 rounded-full p-0.5">
                      <Star size={9} className="text-white fill-white" />
                    </span>
                  )}
                </div>

                {/* Hover overlay with actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />

                <div className="absolute inset-0 flex flex-col justify-between p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Top row: job link */}
                  <div className="flex justify-end">
                    <a
                      href={`/jobs?highlight=${photo.jobId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="p-1.5 bg-white/90 rounded-lg text-gray-600 hover:text-blue-600 transition-colors"
                      title="View originating job"
                    >
                      <ExternalLink size={12} />
                    </a>
                  </div>

                  {/* Bottom row: action buttons */}
                  <div className="flex items-center justify-between gap-1">
                    {/* Featured toggle */}
                    <button
                      onClick={() => handleToggleFeatured(photo)}
                      disabled={isToggling}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        photo.isFeatured
                          ? 'bg-amber-400 text-white hover:bg-amber-500'
                          : 'bg-white/90 text-gray-600 hover:bg-amber-50 hover:text-amber-600'
                      }`}
                      title={photo.isFeatured ? 'Remove from homepage' : 'Feature on homepage'}
                    >
                      {isToggling
                        ? <Loader2 size={11} className="animate-spin" />
                        : <Star size={11} className={photo.isFeatured ? 'fill-white' : ''} />
                      }
                      {photo.isFeatured ? 'Featured' : 'Feature'}
                    </button>

                    {/* Unapprove button */}
                    <button
                      onClick={() => handleUnapprove(photo)}
                      disabled={isToggling}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-white/90 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg text-xs font-medium transition-colors"
                      title="Remove from website"
                    >
                      {isToggling
                        ? <Loader2 size={11} className="animate-spin" />
                        : <EyeOff size={11} />
                      }
                      Remove
                    </button>
                  </div>
                </div>

                {/* Caption if exists */}
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                    <p className="text-[10px] text-white truncate">{photo.caption}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}